// Copyright (c) 2021, Picsa Advisory Services (Pty) Ltd. All rights reserved

import React from 'react'
import PropTypes from 'prop-types'
import { Mutation } from 'react-apollo'
import moment from 'moment'

import {
  Input,
  Button,
  Heading,
  SelectWithData,
  DivisionSelect,
  PayrollCycleSelect,
  KeyValueTable as InputTable
} from '../'

import {
  getDateOfBirthFromSouthAfricanID,
  normalizePhoneNumber
} from '../../utils'

import {
  MUTATION_CLIENT,
  MUTATION_IDENTITY,
  MUTATION_ADDRESS
} from '../../mutations'


export class EmployerEmployeeAdd extends React.Component {
  static propTypes = {
    title: PropTypes.string,
    employerId: PropTypes.string.isRequired,
    onCancel: PropTypes.func,
    onSuccess: PropTypes.func,
    className: PropTypes.string
  }

  static defaultProps = {
    className: ''
  }

  state = {
    identityExistsAlready: false,
    // identity info
    firstName: '',
    lastName: '',
    language: '',
    identityType: '',
    identityValue: '',
    // client info
    division: null,
    payrollcycle: null,
    employmentType: null,
    // address info
    cellphoneNumber: null,
    // validation
    displayInputError: [ false ]
  }

  canSave = () => {
    if ((this.state.identityType === 'za_id' &&
            this.state.identityValue.length === 13 &&
            !isNaN(this.state.identityValue) &&
            this.state.firstName &&
            this.state.lastName &&
            this.state.language &&
            this.state.dateOfBirth
          ) ||
        (this.state.identityType === 'passport' &&
          this.state.firstName &&
          this.state.lastName &&
          this.state.language
        )) {
      return true
    } else {
      let errorFields = []
      errorFields.push(true)

      if (!this.state.identityType) {
        errorFields.push('identityType')
      }
      if (!this.state.identityValue) {
        errorFields.push('identityValue')
      }
      if (this.state.identityType === 'za_id' &&
          (this.state.identityValue.length !== 13 ||
          isNaN(this.state.identityValue))) {
        // something's wrong with id type/value combination input
        errorFields.push('identityValue')
        errorFields.push('identityType')
      }
      if (!this.state.firstName) {
        errorFields.push('firstName')
      }
      if (!this.state.lastName) {
        errorFields.push('lastName')
      }
      if (!this.state.language) {
        errorFields.push('language')
      }

      if (!this.state.division) {
        errorFields.push('division')
      }
      if (!this.state.payrollcycle) {
        errorFields.push('payrollcycle')
      }
      if (!this.state.employmentType) {
        errorFields.push('employmentType')
      }

      this.setState({
        displayInputError: errorFields
      })

      return false
    }
  }

  save = (identityMutate, clientMutate, addressMutate) => {
    if (this.canSave()) {
      let input = {
        firstName: this.state.firstName,
        lastName: this.state.lastName,
        language: !!this.state.language
          ? this.state.language
          : 'eng_za',
        deceased: false,
        identityType: this.state.identityType,
        identityValue: this.state.identityValue
      }

      if (this.state.dateOfBirth) {
        input.dateOfBirth = moment(this.state.dateOfBirth).format('YYYY-MM-DD')
      }

      identityMutate({ variables: { input }})
      .then(this.setState({ displayInputError: [ false ] }))
      .then(({ data }) => {
        // at this point the Identity record has been successfully saved
        clientMutate({
          variables: {
            input: {
              identity: data.identityMutate.identity.id,
              division: this.state.division,
              payrollcycle: this.state.payrollcycle,
              status: 'active'
            }
          }
        })
        .then(({ data }) => {
          // at this point the Client record has been successfully saved
          if (!!this.state.cellphoneNumber) {
            addressMutate({
              variables: {
                input: {
                  identity: data.clientMutate.client.identity.id,
                  addressType: 'msisdn',
                  addressValue: normalizePhoneNumber(this.state.cellphoneNumber),
                  default: true
                }
              }
            })
            .then(this.props.onSuccess)
            .catch(error => console.error('there was an error saving the cellphone number', error))
          }
          else {
            this.props.onSuccess()
          }
        })
        .catch(error => console.error('there was an error saving the client', error))
      })
      .catch(error => console.error('there was an error saving the identity', error))
    }
  }

  autoFillAfterIdentityNumberInput = (e) => {
    let idNumber = e.target.value

    if (this.state.identityType === 'za_id') {
      let dob = getDateOfBirthFromSouthAfricanID(idNumber)

      this.setState({
        identityValue: idNumber,
        dateOfBirth: dob
      })
    }
  }

  handleIdentityTypeInputChange = (e) => {
    let idType = e.target.value

    if (idType === 'za_id'
      && this.state.identityValue !== ''
      && this.state.dateOfBirth === null) {

      let dob = getDateOfBirthFromSouthAfricanID(this.state.identityValue)

      this.setState({
        identityType: idType,
        dateOfBirth: dob
      })
    } else {
      this.setState({
        identityType: idType
      })
    }

    if (idType === 'za_id' && this.state.identityValue.length === 13) {
      // check for existing identity
      this.props.data.refetch({ idNumber: this.state.identityValue })
      // form fields will auto-fill via componentWillReceiveProps if identity exists
    }
  }

  handleInputValueChange = (e) => {
    let field = e.target.id
    this.setState({ [field]: e.target.value })
  }

  getInputForm = () => (
    {
      'Identity type': <SelectWithData
        id='identityType'
        forField='IdentityIdentityType'
        value={this.state.identityType}
        onChange={this.handleIdentityTypeInputChange}
        hasError={this.state.identityValue.length > 0 && this.state.identityType.length === 0}
        exclude={['cipc']}
      />,
      'Identity number':  <Input
        id='identityValue'
        placeholder='Identity number'
        value={this.state.identityValue || ''}
        onChange={this.handleInputValueChange}
        onBlur={this.autoFillAfterIdentityNumberInput}
        hasError={this.state.displayInputError[0] && this.state.displayInputError.includes('identityValue')}
      />,
      'First name': <Input
        id='firstName'
        placeholder='First name'
        value={this.state.firstName || ''}
        onChange={this.handleInputValueChange}
        hasError={this.state.displayInputError[0] && this.state.displayInputError.includes('firstName')}
      />,
      'Last name':  <Input
        id='lastName'
        placeholder='Last name'
        value={this.state.lastName || ''}
        onChange={this.handleInputValueChange}
        hasError={this.state.displayInputError[0] && this.state.displayInputError.includes('lastName')}
      />,
      'Language': <SelectWithData
        id='language'
        forField='IdentityLanguage'
        value={this.state.language}
        onChange={this.handleInputValueChange}
        hasError={this.state.displayInputError[0] && this.state.displayInputError.includes('language')}
      />,
      division: <DivisionSelect
        id='division'
        employer={this.props.employerId}
        value={this.state.division}
        onChange={this.handleInputValueChange}
        hasError={this.state.displayInputError[0] && this.state.displayInputError.includes('division')}
      />,
      'Payroll cycle': <PayrollCycleSelect
        id='payrollcycle'
        employer={this.props.employerId}
        value={this.state.payrollcycle}
        onChange={this.handleInputValueChange}
        hasError={this.state.displayInputError[0] && this.state.displayInputError.includes('payrollcycle')}
      />,
      'Employment type': <SelectWithData
        id='employmentType'
        forField='ClientEmploymentType'
        value={this.state.employmentType}
        onChange={this.handleInputValueChange}
        hasError={this.state.displayInputError[0] && this.state.displayInputError.includes('language')}
      />,
      'Cellphone number': <Input
        id='cellphoneNumber'
        placeholder='Enter number'
        value={this.state.cellphoneNumber}
        onChange={this.handleInputValueChange}
      />
    }
  )

  render() {
    return (
      <div className={this.props.className}>
        {this.props.title &&
          <Heading className='slds-text-align_center'>
            {this.props.title}
          </Heading>
        }
        <InputTable
          data={this.getInputForm()}
          keyColumnSize='1-of-2'
        />
        <div className='slds-m-top_large slds-clearfix'>
          <div className='slds-text-align_center'>
            <Button
              onClick={this.props.onCancel}
              minWidth={120}
              className='u-text-caps'>
              Cancel
            </Button>
            <Mutation mutation={MUTATION_CLIENT}>
              {(clientMutate, { data }) => (
                <Mutation mutation={MUTATION_IDENTITY}>
                  {(identityMutate, { data }) => (
                    <Mutation mutation={MUTATION_ADDRESS}>
                      {(addressMutate, { data }) => (
                        <Button
                          type='brand'
                          onClick={() => this.save(identityMutate, clientMutate, addressMutate)}
                          // disabled
                          minWidth={120}
                          className='u-text-caps'>
                          Save
                        </Button>
                      )}
                    </Mutation>
                  )}
                </Mutation>
              )}
            </Mutation>
          </div>
        </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/employer-employee-add/index.js